import { ZERO, clampPos, perUnitFloor, share, sub, type Money } from './money.js';
import { addDays, addMonths, diffDays, isAfter, type PlainDate } from './date.js';
import { computeAvailable, type AvailableInput } from './available.js';
import {
  cardPurchaseDue, forecast, livingPerDay, minWithOutflow, type Forecast, type CashForecastInput,
} from './forecast.js';
import { bufferStage } from './plan.js';

export interface AffordabilityInput extends AvailableInput {
  /** Сколько стоит покупка. */
  amount: Money;
  /** Когда пользователь хочет купить. По умолчанию — сегодня. */
  date?: PlainDate;
  /** Медиана обычных трат на повседневные нужды в месяц. */
  needBaseline: Money;
  /** Медиана необязательных расходов в месяц. */
  wantBaseline: Money;
  /** Неснижаемый запас: ниже него жить нельзя. */
  safetyBuffer?: Money;
  /** На сколько месяцев вперёд моделируем. */
  horizonMonths?: number;
  /**
   * Трата — это взнос на эту кредитку.
   *
   * Тогда деньги уходят в день взноса, а платежи по карте уменьшаются на ту
   * же сумму. Без этого досрочный взнос на карту считался бы дважды: сегодня
   * и ещё раз в день платежа по выписке.
   */
  paysCard?: string;
  /**
   * Покупка этой кредиткой.
   *
   * Деньги уходят не в день покупки, а в день платежа по выписке, в которую
   * она попадёт: шкаф 17 октября при выписке 16-го и платеже 9-го оплачивается
   * 9 декабря. Прежде проверка считала любую покупку живыми деньгами в день
   * покупки, и советник на данных владельца ответил «шкаф с кредитки не
   * проходит, в начале ноября минус 90 812 ₽» (30.09.2026).
   */
  card?: string;
}

/** Что покупка картой значит для самой карты. */
export interface CardPurchaseEffect {
  statementDate: PlainDate | null;
  paymentDate: PlainDate;
  /** Платёж укладывается в беспроцентный срок. */
  withinGrace: boolean;
  /** Свободный лимит сейчас — до покупки. */
  limitLeft: Money;
}

export type Verdict = 'safe' | 'tight' | 'unsafe';

export interface AffordabilityResult {
  verdict: Verdict;
  /** Сколько останется до следующей зарплаты, если купить в желаемую дату. */
  leftAfterPurchase: Money;
  /** Минимальный остаток по всей траектории — он и определяет вердикт. */
  minBalance: Money;
  minBalanceDate: PlainDate;
  /** Придётся ли тронуть кредитку. */
  creditNeeded: Money;
  /** Ближайшая дата, когда покупка безопасна. */
  safeDate: PlainDate | null;
  /** Сколько откладывать в месяц, чтобы купить в желаемую дату без риска. */
  monthlyToHitDate: Money | null;
  /** Ломает ли покупка критичную цель. */
  breaksCriticalGoal: boolean;
  /** Опустится ли подушка ниже первого порога. */
  dropsBufferBelowCritical: boolean;
  daysToHorizon: number;
  /** Только у покупки картой. */
  card?: CardPurchaseEffect;
  /** Чего прогноз не знает — обязательно к показу рядом с ответом. */
  limits: string[];
}

/**
 * «Могу ли я себе это позволить?» (PRD §2.4).
 *
 * Отвечает не «да/нет», а **решением с датой**. Ответ строится по прогнозу
 * денежного потока, а не по остатку бюджета категории: «превышен бюджет
 * категории Мебель» не отвечает на вопрос человека, а «останется 24 000 ₽ на
 * 18 дней, безопаснее в ноябре» — отвечает.
 *
 * Числа считает эта функция целиком. Формулировка ответа — задача слоя выше
 * (PRD §10.5): модель получает готовый расчёт и превращает его в текст.
 * Иначе появляется класс отказов «приложение сказало, что могу, а денег не
 * хватило», который убивает доверие безвозвратно.
 */
export function checkAffordability(input: AffordabilityInput): AffordabilityResult {
  const purchaseDate = input.date ?? input.today;
  const safety = input.safetyBuffer ?? share(input.needBaseline, 0.1);
  const months = input.horizonMonths ?? 3;

  const base = computeAvailable(input);
  // Покупка картой до прихода денег не забирает, если её платёж позже.
  const card = input.card ? cardEffect(input, purchaseDate) : null;
  // Прогноз строится один раз: и траектория покупки, и поиск безопасной даты
  // считаются по нему, а не пересобирают будущее на каждый день. Платёж по
  // карте за горизонтом прогноз бы не увидел и назвал покупку безопасной.
  const horizon = addMonths(input.today, months);
  const to = card && isAfter(card.paymentDate, horizon) ? card.paymentDate : horizon;
  const plan = forecast(forecastInputOf(input, to));
  const traj = trajectory(input, plan, purchaseDate);

  const leftAfterPurchase = card && isAfter(card.paymentDate, base.horizonDate)
    ? base.available
    : sub(base.available, input.amount);
  const creditNeeded = clampPos(sub(safety, traj.minBalance));

  const buffer = input.goals.find((g) => g.kind === 'emergency_fund' && g.status === 'active');
  const dropsBufferBelowCritical = buffer
    ? bufferStage(clampPos(sub(buffer.currentAmount, clampPos(neg(leftAfterPurchase)))), input.needBaseline) === 'critical'
      && bufferStage(buffer.currentAmount, input.needBaseline) !== 'critical'
    : false;

  const breaksCriticalGoal = input.goals.some(
    (g) => g.status === 'active' && g.priority === 'critical' && leftAfterPurchase < ZERO,
  );

  let verdict: Verdict;
  if (traj.minBalance >= safety && leftAfterPurchase >= ZERO) verdict = 'safe';
  else if (creditNeeded <= ZERO && leftAfterPurchase >= ZERO) verdict = 'tight';
  else verdict = 'unsafe';
  // Не влезает в лимит — картой не купить вовсе, что бы ни говорил остаток.
  if (card && card.limitLeft < input.amount) verdict = 'unsafe';

  // Если после покупки остаётся меньше, чем обычно уходит на повседневные нужды за
  // оставшиеся дни, — это «тесно» даже при формально положительном остатке.
  if (verdict === 'safe') {
    const expectedSpend = expectedSpendUntil(input, base.daysToHorizon);
    if (leftAfterPurchase < expectedSpend) verdict = 'tight';
  }

  return {
    verdict,
    leftAfterPurchase,
    minBalance: traj.minBalance,
    minBalanceDate: traj.minBalanceDate,
    creditNeeded,
    safeDate: findSafeDate(input, plan, safety),
    // Копить под покупку картой незачем: её оплатит платёж по выписке.
    monthlyToHitDate: card ? null : monthlyToHitDate(input, purchaseDate, input.amount, base.available),
    ...(card ? { card } : {}),
    breaksCriticalGoal,
    dropsBufferBelowCritical,
    daysToHorizon: base.daysToHorizon,
    limits: plan.limits,
  };
}

const neg = (m: Money): Money => (m < 0 ? (-m as Money) : ZERO);

/** Ожидаемые обычные траты за N дней — по медианам, а не по нулю. */
export function expectedSpendUntil(input: AffordabilityInput, days: number): Money {
  return share(livingPerDay(input.needBaseline, input.wantBaseline), days);
}

interface Trajectory {
  minBalance: Money;
  minBalanceDate: PlainDate;
}

/**
 * Вход прогноза из входа проверки.
 *
 * Прогноз — общий модуль (`forecast.ts`): поступления, обязательные платежи,
 * платежи по кредиткам и обычная жизнь по дням. Прежде здесь был свой цикл,
 * и он не знал о картах: у владельца 9 октября по выписке 149 374,93 ₽, а
 * проверка покупки считала, что этих денег никто не просит.
 */
function forecastInputOf(input: AffordabilityInput, to: PlainDate): CashForecastInput {
  return {
    today: input.today,
    to,
    accounts: input.accounts,
    creditTerms: input.creditTerms,
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    needBaseline: input.needBaseline,
    wantBaseline: input.wantBaseline,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
    ...(input.incomeFacts ? { incomeFacts: input.incomeFacts } : {}),
  };
}

/**
 * Самый низкий остаток, если потратить в этот день.
 *
 * Обычная покупка сдвигает прогноз вниз с её дня — это считается по готовому
 * прогнозу. Взнос на карту меняет ещё и её будущие платежи, поэтому для него
 * прогноз собирается заново.
 */
function trajectory(input: AffordabilityInput, plan: Forecast, date: PlainDate): Trajectory {
  if (!input.paysCard && !input.card) return minWithOutflow(plan, date, input.amount);
  const f = forecast({
    ...forecastInputOf(input, plan.to),
    extra: [input.card
      ? { date, amount: input.amount, label: 'Покупка картой', card: input.card }
      : { date, amount: input.amount, label: 'Взнос на карту', paysCard: input.paysCard! }],
  });
  return { minBalance: f.minBalance, minBalanceDate: f.minBalanceDate };
}

/** Выписка, платёж и лимит для покупки картой. */
function cardEffect(input: AffordabilityInput, date: PlainDate): CardPurchaseEffect | null {
  const account = input.accounts.find((a) => a.id === input.card && a.kind === 'credit_card');
  const terms = input.creditTerms.find((t) => t.accountId === input.card);
  if (!account || !terms) return null;
  const due = cardPurchaseDue(terms, date);
  return {
    statementDate: due.statementDate,
    paymentDate: due.paymentDate,
    withinGrace: due.withinGrace,
    limitLeft: clampPos(sub(terms.limit, clampPos(neg(account.balance)))),
  };
}

/** Ближайшая дата, когда покупка перестаёт быть рискованной. */
function findSafeDate(
  input: AffordabilityInput,
  plan: Forecast,
  safety: Money,
): PlainDate | null {
  const limit = diffDays(plan.today, plan.to);
  for (let i = 0; i <= limit; i += 1) {
    const day = addDays(plan.today, i);
    if (trajectory(input, plan, day).minBalance >= safety) return day;
  }
  return null;
}

/** Сколько откладывать в месяц, чтобы успеть к желаемой дате. */
function monthlyToHitDate(
  input: AffordabilityInput,
  date: PlainDate,
  amount: Money,
  available: Money,
): Money | null {
  const gap = clampPos(sub(amount, clampPos(available)));
  if (gap <= ZERO) return null;
  const days = diffDays(input.today, date);
  if (days <= 0) return gap;
  const months = Math.max(1, Math.round(days / 30));
  return share(gap, 1 / months);
}

/**
 * Данные для формулировки ответа: вердикт → последствие → альтернатива.
 * Порядок именно такой, и кнопка «всё равно купить» присутствует всегда —
 * приложение советник, а не родитель.
 */
export interface AffordabilityNarrative {
  verdict: Verdict;
  consequence: { leftAfterPurchase: Money; days: number; expectedSpend: Money; creditNeeded: Money };
  alternative: { safeDate: PlainDate | null; monthly: Money | null };
  actions: Array<'create_goal' | 'plan_expense' | 'buy_anyway' | 'shift_date'>;
}

export function narrativeInput(
  input: AffordabilityInput,
  r: AffordabilityResult,
): AffordabilityNarrative {
  const actions: AffordabilityNarrative['actions'] = [];
  if (r.verdict !== 'safe' && r.monthlyToHitDate) actions.push('create_goal');
  if (r.verdict !== 'safe' && r.safeDate) actions.push('shift_date');
  if (r.verdict === 'safe') actions.push('plan_expense');
  actions.push('buy_anyway'); // всегда последней и всегда есть

  return {
    verdict: r.verdict,
    consequence: {
      leftAfterPurchase: r.leftAfterPurchase,
      days: r.daysToHorizon,
      expectedSpend: expectedSpendUntil(input, r.daysToHorizon),
      creditNeeded: r.creditNeeded,
    },
    alternative: { safeDate: r.safeDate, monthly: r.monthlyToHitDate },
    actions,
  };
}

/**
 * Разрешённый темп против обычного.
 *
 * Выпало из анализа и оказалось важнее ожидаемого: «можно 2 350 ₽ в день» само
 * по себе не отвечает на вопрос «много это или мало». Ответ появляется только
 * в сравнении с тем, как человек тратит обычно. Если обычный темп выше
 * разрешённого, деньги кончатся до зарплаты при любой отдельно взятой покупке —
 * и сказать об этом надо до покупки, а не после.
 */
export interface Pace {
  allowedPerDay: Money;
  usualPerDay: Money;
  /** Обычный темп выше разрешённого — до зарплаты не дотянуть. */
  overPace: boolean;
  /** На сколько дней хватит при обычном темпе. */
  daysAtUsualPace: number;
  daysLeft: number;
}

export function pace(input: {
  available: Money;
  daysLeft: number;
  needBaseline: Money;
  wantBaseline: Money;
}): Pace {
  const usualPerDay = livingPerDay(input.needBaseline, input.wantBaseline);
  const allowedPerDay = input.daysLeft > 0 ? perUnitFloor(clampPos(input.available), input.daysLeft, 100) : ZERO;
  const daysAtUsualPace = usualPerDay > ZERO ? Math.floor(clampPos(input.available) / usualPerDay) : input.daysLeft;
  return {
    allowedPerDay,
    usualPerDay,
    overPace: usualPerDay > allowedPerDay,
    daysAtUsualPace,
    daysLeft: input.daysLeft,
  };
}
